import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ResultadoParcialResponse } from 'api/EventoAssociado/pauta';

import { Divider, Grid, Typography } from '@material-ui/core';
import Loading from 'components/Modal/__LEGACY__/AbrirVotacaoPautaModal/Loading';
import GenericModal, {
  IGenericModalProps,
} from 'components/Modal/__LEGACY__/GenericModal/GenericModal';
import ProgressBar from 'components/__ASSESSOR__/ProgressBar/ProgressBar';

import { AssembleiaTipo } from 'helpers/enums/AssembleiaTipo';

import { assembleiasAssociadoActionsResultadoParcialAGO } from 'store/modules/AssembleiasAssociadoActions';
import { modalsActionsClose } from 'store/modules/ModalsActions';
import { IModalProps } from 'store/modules/ModalsReducer';

import ErrorHandler from 'helpers/utils/ErrorHandler';

import { toFixedPercentage } from 'helpers/utils/StringUtils';
import translation from 'locales/translation';
import useStyle from './style';

export interface RespostaPautaAGOGenericModalProps extends IGenericModalProps {
  key: string;
  idPauta: number;
  isParcial: boolean;
  tipoAssembleia: AssembleiaTipo;
}

interface Props extends IModalProps {
  genericProps: RespostaPautaAGOGenericModalProps;
}

export default function RespostaPautaAGO({ genericProps, open }: Props) {
  const classes = useStyle();
  const dispatch = useDispatch();
  const [resultado, setResultado] = useState<ResultadoParcialResponse>();

  const handleClose = useCallback(() => {
    dispatch(modalsActionsClose(genericProps.key));
  }, [dispatch, genericProps]);

  const isMajor = useCallback(
    (idItemPauta: number) => {
      if (!resultado) return false;
      const descendant = Array.from(resultado.itens).sort((a, b) => {
        return b.percentual - a.percentual;
      });
      return descendant[0].idItemPauta === idItemPauta;
    },
    [resultado]
  );

  useEffect(() => {
    if (!resultado) {
      dispatch(
        assembleiasAssociadoActionsResultadoParcialAGO(
          genericProps.idPauta,
          (response) => {
            setResultado(response);
          },
          ErrorHandler
        )
      );
    }
  }, [dispatch, genericProps, resultado]);

  return (
    <GenericModal
      open={open ?? false}
      title={
        translation.RespostaPautaAGO.title[`${genericProps.tipoAssembleia}`]
      }
      genericProps={{
        maxWidth: 'sm',
        onClose: handleClose,
        DialogContentProps: {
          className: classes.root,
        },
      }}
      content={
        <React.Fragment>
          {!resultado && <Loading inline />}
          <Typography component='p' className={classes.title}>
            {resultado?.titulo}
          </Typography>
          <Divider />
          {(resultado?.itens || [])
            .sort((itemA, itemB) => {
              if (itemA.abstencao) return 1;
              if (itemB.abstencao) return -1;
              return itemA.idItemPauta - itemB.idItemPauta;
            })
            .map((item, i, array) => (
              <Grid key={item.idItemPauta} container className={classes.item}>
                <Grid item xs={12} sm={8}>
                  <Typography className={classes.itemText}>
                    {isMajor(item.idItemPauta) ? (
                      <strong>{item.descricao}</strong>
                    ) : (
                      item.descricao
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={9} sm={2}>
                  <Typography className={classes.itemPerc}>
                    {translation.RespostaPautaAGO.nucleo(item.votos)}
                  </Typography>
                </Grid>
                <Grid item xs={3} sm={2}>
                  <Typography className={classes.itemPerc}>
                    {toFixedPercentage(item.percentual)
                      .toString()
                      .replace('.', ',')}
                    %
                  </Typography>
                </Grid>
                <Grid item xs={12} className={classes.itemProgress}>
                  <ProgressBar genericProps={{ percent: item.percentual }} />
                </Grid>
                {array.length > i + 1 && (
                  <Grid item xs={12} className={classes.responsiveDivider}>
                    <Divider />
                  </Grid>
                )}
              </Grid>
            ))}
          {[AssembleiaTipo.Nucleo, AssembleiaTipo.Reuniao].indexOf(
            genericProps.tipoAssembleia!
          ) > -1 ? (
            <Typography component='p' className={classes.advice}>
              {
                translation.RespostaPautaAGO[
                  genericProps.isParcial ? 'parcial' : 'final'
                ]
              }
            </Typography>
          ) : undefined}
        </React.Fragment>
      }
    />
  );
}
